import { NextPage } from "next";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useEffect } from "react";
import { useAuth } from "utils/auth";

import {
  ArticleInitialPropsQueryVariables,
  ArticleQuery,
  ArticleQueryVariables,
} from "types/api";

import { AdTemplatesEnum, registerAdsPageView } from "utils/ads";
import { trackArticlePageView } from "utils/analytics";
import checkArticleRenderSupport from "utils/checkArticleRenderSupport";

import PageLayout from "components/PageLayout";
import ArticleLayout from "components/article/ArticleLayout";
import ArticleJsonLd from "components/article/ArticleJsonLd";
import ArticleMetadata from "components/article/ArticleMetadata";
import ArticleHeader from "components/article/ArticleHeader";
import ArticleDek from "components/article/ArticleDek";
import ArticleByline from "components/article/ArticleByline";
import IdeasArticleByline from "components/article/IdeasArticleByline";
import ArticleDateline from "components/article/ArticleDateline";
import ArticleLeadArt from "components/article/ArticleLeadArt";
import EditorsNote from "components/article/EditorsNote";
import ArticleContent from "components/article/ArticleContent";
import ArticleBio from "components/article/ArticleBio";
import ArticleFoundationLine from "components/article/ArticleFoundationLine";
import ArticleBentoRecirc from "components/article/ArticleBentoRecirc";
import ArticleRecirc from "components/article/ArticleRecirc";

import { withData, useQuery, gql, NextUrqlPageContext } from "utils/urql";
import { ArticleInitialPropsQuery } from "../../../types/api";
import { throwCorrectStatus, NotFound } from "utils/error";

const DynamicPaywall = dynamic(() => import("components/paywall/Paywall"), {
  ssr: false,
});

const DynamicNonMeteredNudge = dynamic(
  () => import("components/NonMeteredNudge"),
  {
    ssr: false,
  }
);

const useTrackPageView = ({ article }: ArticleQuery) => {
  const categorySlugs = article.categories.map(({ slug }) => slug);
  const channelSlugs = article.channels.map(({ slug }) => slug);
  const isMagArticle = article.__typename === "MagazineArticle" ? true : false;
  const issue =
    article.__typename === "MagazineArticle" ? article.issue : undefined;

  useEffect(() => {
    // Track the article in GTM
    trackArticlePageView({ article });

    // Register the pageview in ads.js
    registerAdsPageView({
      channel: article.primaryChannel?.slug || "", // primary Channel
      template: AdTemplatesEnum.article_standard,
      datePublished: article.datePublished || "",
      globalTargeting: {
        title: article.slug || "", // article slug
        grapeshot_segments: article.grapeshot?.segments || [], // brand safety list
        cat: categorySlugs, // slugs of all the categories the article is tagged with
        rubric: article.primaryChannel?.displayName || "", // primary channel name
        id: article.id.split(":")[1], // story ID
        motivation: article.editorialMotivation?.slug || "", // Editorial motivation slug
        xpost: channelSlugs, // slugs of all channels the article is tagged with
        type: isMagArticle ? "mag-reg" : "non-mag-reg", // is it a magazine article
        iss: issue?.slug || undefined, // issue slug if it's a magazine, or undefined
        issue_name: issue?.issueName || undefined, // issue name if it's a magazine, or undefined
        report: article.editorialProject?.slug || "", // Special report slug
        template: AdTemplatesEnum.article_standard, // pass template name
      },
    });
  }, [article.id]); //eslint-disable-line react-hooks/exhaustive-deps
};

const ARTICLE_QUERY = gql`
  ${ArticleLeadArt.fragment}
  ${EditorsNote.fragment}
  ${ArticleContent.fragment}
  ${ArticleBio.fragment}
  ${ArticleHeader.fragment}
  ${ArticleMetadata.fragment}
  ${ArticleDek.fragment}
  ${ArticleByline.fragment}
  ${IdeasArticleByline.fragment}
  ${ArticleDateline.fragment}
  ${ArticleFoundationLine.fragment}
  ${ArticleJsonLd.fragment}
  ${ArticleBentoRecirc.fragment}
  ${ArticleRecirc.fragment}

  query ArticleQuery($id: ID!, $preview: String) {
    article(id: $id, preview: $preview) {
      __typename
      id
      datePublished
      title
      slug
      wordCount
      layout
      isFreelance
      isPython3
      preview
      watsonInfo {
        topCategory {
          full
          one
          two
          three
          four
        }
        categories
      }
      grapeshot {
        segments
      }
      editorialMotivation {
        slug
      }
      editorialProject {
        slug
        displayName
        ...articleBentoRecirc
        ...articleFoundationLine
      }
      ...articleMetadata
      ...articleHeader
      ...articleDek
      ...articleDateline
      ...articleBio
      ...ideasArticleByline
      ...articleByline
      ...articleJsonLdData

      htmlContentSections {
        ...articleContent
      }
      leadArt {
        ...articleLeadArt
      }
      embeds {
        type
        src
      }
      primaryChannel {
        slug
        displayName
      }
      categories {
        slug
      }
      channels {
        slug
      }
      editorsNote {
        ...editorsNote
      }
      ... on MagazineArticle {
        issue {
          issueName
          slug
        }
      }
    }
  }
`;

const ArticleDisplay = ({ data }: { data: ArticleQuery }) => {
  const { hasPaywallAccess } = useAuth();

  checkArticleRenderSupport(data);

  useTrackPageView(data);
  return (
    <>
      <Head>
        <title>
          {data.article.seoTitle || data.article.title} - The Atlantic
        </title>
      </Head>
      <ArticleMetadata {...data.article} />
      <ArticleJsonLd {...data.article} />
      <ArticleLayout {...data.article} />

      {/* // import paywall if article is metered; if not, import non-metered nudge */}
      {data.article.hasMeter && !hasPaywallAccess ? (
        <DynamicPaywall />
      ) : (
        <DynamicNonMeteredNudge {...data.article} />
      )}
    </>
  );
};

const Article: NextPage = () => {
  const { query } = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [year, month, slug, id] = query.article as Array<string>;
  const preview = typeof query.preview === "string" ? query.preview : null;

  const [{ error, data }] = useQuery<ArticleQuery, ArticleQueryVariables>({
    query: ARTICLE_QUERY,
    variables: {
      id,
      preview,
    },
  });

  if (error) {
    throwCorrectStatus({ error });
  }

  return (
    <PageLayout>
      {data && (
        <ArticleDisplay key={`article-${data.article.id}`} data={data} />
      )}
    </PageLayout>
  );
};

const ARTICLE_INITIAL_PROPS_QUERY = gql`
  query ArticleInitialPropsQuery($id: ID!, $preview: String) {
    article(id: $id, preview: $preview) {
      id
      layout
      hasMeter
      url
    }
  }
`;

Article.getInitialProps = async ({
  urqlClient,
  query,
  req,
  res,
}: NextUrqlPageContext) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [year, month, slug, id] = query.article as Array<string>;

  const articleUrlFormat = new RegExp(
    /\/[a-z]+\/archive\/\d{4}\/\d{2}\/[a-z\-\d]+\/\d+/
  );

  //If input url doesnt match the standard format, redirect to 404 page.
  if (req?.url) {
    const requestMatchesStandardFormat = req?.url?.match(articleUrlFormat);
    if (!requestMatchesStandardFormat) {
      throw new NotFound();
    }
  }
  const preview = typeof query.preview === "string" ? query.preview : null;

  // Run an initial query to gather information required to set response headers
  // for paywall, caching, etc.
  const result = await urqlClient
    .query<ArticleInitialPropsQuery, ArticleInitialPropsQueryVariables>(
      ARTICLE_INITIAL_PROPS_QUERY,
      { id, preview }
    )
    .toPromise();

  // Checking the existence of `res` ensures code blocks only execute on
  // the server.
  if (res) {
    // Set Surrogate-Key header for Fastly cached purgin
    res.setHeader("Surrogate-Key", `article-id-${id}`);

    // If there is a preview query param, disable caching.
    if (query.preview) {
      res.setHeader("Cache-Control", "no-store");
    }
  }
  if (res && result.data?.article) {
    checkArticleRenderSupport(result.data);

    const { article } = result.data;
    const rawId = article.id.split(":")[1];

    // If there haven't been any errors, set Cache-Control headers to cache the
    // article for three minutes in proxies and set some sane defaults for stale
    // caching.
    if (!query.preview) {
      res.setHeader(
        "Cache-Control",
        "s-maxage=180, stale-while-revalidate=3600, stale-if-error=86400"
      );
    }

    if (article.hasMeter) {
      res.setHeader("X-Is-Metered", "1");
      res.setHeader("X-Meter-Content", rawId);

      // Generate a date representing the first day of next month.
      // Date(2020, 12, 1) is January 1st, 2021.
      const today = new Date();
      const meterExpires = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        1
      );
      res.setHeader("X-Meter-Expires", meterExpires.toUTCString());
    }

    //If request url doesn't include api-supplied article url,
    //redirect to correct article url (with original query params).
    const articlePathname = article.url.split("theatlantic.com")[1];
    if (req?.url?.indexOf(articlePathname) === -1) {
      const queryParams = req.url.split(articleUrlFormat)[1].slice(1);

      res.setHeader("Location", `${articlePathname}${queryParams}`);
      res.statusCode = 302;
      res.end();
    }
  }
};

export default withData(Article);
